import { type Component, createEffect, createMemo, createSignal, createUniqueId, For, onCleanup, onMount, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import { useFieldError } from '~/hooks/useFieldError'
import type { ChipSizeType } from '~/types/Chip'
import type { MainSizesType } from '~/types/components'
import type { OptionType, SelectPropsType, valueType } from '~/types/Select'
import { controlSize } from '~/utils/controlSize'
import Button from './Button'
import Checkbox from './Checkbox'
import Chip from './Chip'
import FieldMessage from './FieldMessage'
import { useFormContext } from './Form'
import Icon from './Icon'
import Input from './Input'

/** Talla de los chips del modo múltiple, que no tiene equivalente en la escala común. */
const chipBySize: Record<MainSizesType, ChipSizeType> = { xs: 'xs', sm: 'sm', md: 'sm', lg: 'md', xl: 'lg' }

const Select: Component<SelectPropsType> = props => {
  const [isOpen, setIsOpen] = createSignal(false)
  const [_isFocused, setIsFocused] = createSignal(false)
  const [selectedState, setSelectedState] = createSignal<valueType>(props.value || (props.multiple ? [] : ''))
  const [dropdownStyle, setDropdownStyle] = createSignal<{
    top: number
    left: number
    width: number
    showAbove: boolean
  }>({ top: 0, left: 0, width: 0, showAbove: false })
  const id = createUniqueId()
  const labelId = createUniqueId()
  let hiddenRef: HTMLInputElement | undefined
  let containerRef: HTMLDivElement | undefined
  let triggerRef: HTMLDivElement | undefined
  let menuRef: HTMLDivElement | undefined
  const [search, setSearch] = createSignal('')

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto.
  const formCtx = useFormContext()
  const resolvedError = useFieldError(
    () => props.name,
    () => props.error
  )

  /**
   * El valor no vive en el DOM sino en `selectedState`, así que el `<Form>` no
   * puede ni leerlo ni escribirlo por su cuenta: escribir en el input oculto no
   * sirve de nada porque el siguiente render lo pisa. Registrándose, `setValue`,
   * `reset` y los valores iniciales que llegan tarde llegan hasta aquí.
   */
  onMount(() => {
    if (!props.name || !formCtx) return

    formCtx.registerField(props.name, {
      // En simple, la misma cadena que llevaba el input oculto: el valor de una
      // opción puede ser número y los esquemas que ya existen esperan texto.
      // La lista sí viaja como lista, que es lo que el DOM no sabía dar.
      getValue: () => (props.multiple ? (Array.isArray(selectedState()) ? selectedState() : []) : String(selectedState() ?? '')),
      setValue: value => {
        if (value === undefined || value === null) {
          setSelectedState(props.multiple ? [] : '')
          return
        }
        if (props.multiple) {
          setSelectedState(Array.isArray(value) ? (value as (string | number)[]) : [value as string | number])
          return
        }
        setSelectedState(value as string | number)
      }
    })

    onCleanup(() => {
      if (props.name) formCtx.unregisterField(props.name)
    })
  })

  /**
   * Un desplegable no dispara `focusout` desde su input oculto, así que el
   * `<Form>` nunca lo daba por tocado y su error no salía hasta pulsar Guardar.
   */
  const touch = () => {
    if (props.name) formCtx?.touchField(props.name)
  }

  createEffect(() => {
    if (props.value !== undefined) {
      setSelectedState(props.value)
    }
  })

  /**
   * El valor viaja en un input oculto, y un oculto no dispara eventos por sí
   * solo. Sin esto, un `<Form>` que envuelva el Select no se entera de que ha
   * cambiado hasta el envío: la validación en vivo se quedaba congelada.
   *
   * Sólo se avisa cuando el valor cambia de verdad: emitir en cada pasada
   * realimentaría al formulario, que a su vez vuelve a fijar `value`.
   */
  let lastEmitted: string | null = null
  createEffect(() => {
    const current = props.multiple ? JSON.stringify(selectedState() || []) : String(selectedState() ?? '')
    if (!props.name || !hiddenRef) return
    if (current === lastEmitted) return

    lastEmitted = current
    // Los dos: el `<Form>` escucha `input` para revalidar en vivo y `change`
    // para el resto. Con sólo uno, la validación se quedaba congelada.
    hiddenRef.dispatchEvent(new Event('input', { bubbles: true }))
    hiddenRef.dispatchEvent(new Event('change', { bubbles: true }))
  })

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef && !containerRef.contains(event.target as Node) && menuRef && !menuRef.contains(event.target as Node)) {
      if (isOpen()) touch()
      setIsOpen(false)
      setSearch('')
    }
  }

  let rafId: number | undefined
  const updateDropdownPosition = () => {
    if (!triggerRef) return
    if (rafId) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(() => {
      if (!triggerRef) return
      const rect = triggerRef.getBoundingClientRect()
      let menuHeight = 300
      if (menuRef) {
        const m = menuRef.getBoundingClientRect()
        menuHeight = Math.min(Math.max(m.height, 0), 300)
      } else {
        const optionsCount = filteredOptions().length || props.options.length
        const itemHeight = props.size === 'xs' ? 32 : props.size === 'sm' ? 36 : props.size === 'lg' ? 44 : props.size === 'xl' ? 52 : 40
        const estimatedHeight = Math.min(optionsCount * itemHeight + (props.autocomplete ? 60 : 16), 300)
        menuHeight = estimatedHeight
      }
      const viewportH = window.innerHeight
      const viewportW = window.innerWidth
      const margin = 8
      // Pequeño aire entre el trigger y el menú: la sombra del lado del trigger
      // quedaba atrapada entre ambos y se veía como una franja flotante.
      const gap = 4
      const spaceBelow = viewportH - rect.bottom
      const spaceAbove = rect.top
      const showAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow
      let top = showAbove ? rect.top - menuHeight - gap : rect.bottom + gap
      let left = rect.left
      top = Math.max(margin, Math.min(top, viewportH - margin - menuHeight))
      left = Math.max(margin, Math.min(left, viewportW - margin - rect.width))
      setDropdownStyle({ top, left, width: rect.width, showAbove })
      rafId = undefined
    })
  }

  const toggleDropdown = () => {
    if (!props.disabled) {
      const newIsOpen = !isOpen()
      setIsOpen(newIsOpen)
      if (newIsOpen) {
        updateDropdownPosition()
        setTimeout(() => {
          if (isOpen()) updateDropdownPosition()
        }, 1)
      } else {
        setSearch('')
      }
    }
  }

  onMount(() => {
    const handler = () => {
      if (isOpen()) updateDropdownPosition()
    }
    window.addEventListener('resize', handler)
    window.addEventListener('scroll', handler, true)
    onCleanup(() => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('scroll', handler, true)
      if (rafId) cancelAnimationFrame(rafId)
    })
  })

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside)
  })
  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside)
  })

  const DropdownMenu = () => (
    <Portal>
      <Show when={isOpen()}>
        <div
          ref={el => {
            menuRef = el
            if (el && isOpen()) {
              setTimeout(() => updateDropdownPosition(), 1)
            }
          }}
          role="listbox"
          class={twMerge('z-9999 bg-surface-muted rounded-lg border border-border py-2 pt-0', shadowByDirection(), 'transition-opacity duration-150 ease-out', isOpen() ? 'opacity-100' : 'opacity-0')}
          style={{
            position: 'fixed',
            top: `${dropdownStyle().top}px`,
            left: `${dropdownStyle().left}px`,
            width: `${dropdownStyle().width}px`,
            'max-height': '300px',
            'overflow-y': 'auto'
          }}
        >
          <Show when={props.autocomplete}>
            <div class="px-3 py-2 sticky top-0 z-10 bg-surface-muted border-b border-border">
              <Input variant="outlined" label="Buscar..." value={search()} onInput={e => setSearch(e.currentTarget.value)} autofocus />
            </div>
          </Show>
          <Show
            when={filteredOptions().length > 0}
            fallback={
              <div class="px-3 py-4 text-center text-foreground-subtle text-sm">
                <Show when={props.autocomplete && search().trim() !== ''} fallback="No hay opciones disponibles">
                  No se encontraron resultados para "{search()}"
                </Show>
              </div>
            }
          >
            <For each={filteredOptions()}>
              {option => {
                const isSelected = Array.isArray(selectedState()) ? (selectedState() as (string | number)[]).includes(option.value) : selectedState() === option.value
                const rowClasses = twMerge(
                  itemPaddingBySize(),
                  isSelected ? 'bg-transparent text-primary' : 'hover:bg-surface-hover text-foreground',
                  option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  'flex items-center gap-2'
                )
                return (
                  <div role="option" aria-selected={isSelected} class={rowClasses} onClick={() => !option.disabled && handleSelect(option)}>
                    {props.multiple && (
                      <div class="flex items-center justify-center w-14">
                        {/* Indicador lateral: siempre reservar espacio para evitar jump */}

                        <div class="flex items-center justify-center">
                          <Checkbox checked={Array.isArray(selectedState()) && (selectedState() as (string | number)[]).includes(option.value)} disabled={option.disabled} />
                        </div>
                      </div>
                    )}
                    <div class="flex flex-col">
                      <span class={twMerge(isSelected ? 'text-primary font-medium' : 'text-foreground')}>{option.title}</span>
                      {option.subtitle && <span class={twMerge('text-xs', isSelected ? 'text-primary/90' : 'text-foreground-subtle')}>{option.subtitle}</span>}
                    </div>
                  </div>
                )
              }}
            </For>
          </Show>
        </div>
      </Show>
    </Portal>
  )

  const getSelectedLabels = createMemo(() => {
    const currentSelected = selectedState()
    if (props.multiple) {
      const values = Array.isArray(currentSelected) ? currentSelected : []
      return values
        .map(v => {
          const option = props.options.find(opt => opt.value === v)
          return option ? { value: option.value, label: option.title } : null
        })
        .filter(Boolean)
    }
    const selectedOption = props.options.find(opt => opt.value === currentSelected)
    return selectedOption ? [{ value: selectedOption.value, label: selectedOption.title }] : []
  })

  const hasValue = createMemo(() => {
    const currentSelected = selectedState()
    if (props.multiple) {
      return Array.isArray(currentSelected) && currentSelected.length > 0
    }
    return currentSelected !== '' && currentSelected !== undefined && currentSelected !== null
  })

  const handleSelect = (option: OptionType) => {
    let newValue: valueType
    if (props.multiple) {
      const current = Array.isArray(selectedState()) ? [...(selectedState() as (string | number)[])] : []
      const index = current.indexOf(option.value)
      if (index > -1) {
        current.splice(index, 1)
      } else {
        current.push(option.value)
      }
      newValue = current
    } else {
      newValue = option.value
      setIsOpen(false)
    }
    setSelectedState(newValue)
    touch()
    props.onChange?.(newValue)
  }

  const removeSelected = (optionValue: string | number, e: MouseEvent) => {
    e.stopPropagation()
    if (props.multiple) {
      const current = Array.isArray(selectedState()) ? (selectedState() as (string | number)[]) : []
      const newSelected = current.filter(v => v !== optionValue)
      setSelectedState(newSelected)
      props.onChange?.(newSelected)
    } else {
      setSelectedState('')
      props.onChange?.('')
    }
  }

  const getVariantClasses = () => {
    switch (props.variant) {
      case 'outlined':
        return 'border border-border-strong bg-transparent'
      default:
        return 'bg-surface-muted border-border-strong'
    }
  }

  const filteredOptions = createMemo(() => {
    if (!props.autocomplete) return props.options
    const term = search().toLowerCase()
    return props.options.filter(opt => opt.title.toLowerCase().includes(term) || opt.subtitle?.toLowerCase().includes(term))
  })

  // Al filtrar cambia el alto de la lista: medir de nuevo para que el menú se
  // ajuste y se reposicione (puede cambiar hasta el lado en el que abre).
  createEffect(() => {
    filteredOptions()
    if (isOpen()) updateDropdownPosition()
  })

  // Sombra dirigida: se desplaza hacia donde "flota" el menú y no se dibuja en
  // el lado pegado al trigger, que quedaba con una franja de sombra encima.
  const shadowByDirection = createMemo(() =>
    dropdownStyle().showAbove
      ? 'shadow-[0_-2px_4px_rgba(0,0,0,0.03),0_-8px_16px_rgba(0,0,0,0.06),0_-20px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_-2px_4px_rgba(0,0,0,0.35),0_-8px_16px_rgba(0,0,0,0.45),0_-20px_40px_rgba(0,0,0,0.5)]'
      : 'shadow-[0_2px_4px_rgba(0,0,0,0.03),0_8px_16px_rgba(0,0,0,0.06),0_20px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.35),0_8px_16px_rgba(0,0,0,0.45),0_20px_40px_rgba(0,0,0,0.5)]'
  )

  const sizeConfig = createMemo(() => controlSize(props.size))
  const chipSize = createMemo(() => chipBySize[props.size ?? 'md'] ?? 'sm')

  /**
   * El disparador mide su talla exacta salvo en el modo múltiple con chips a la
   * vista, que es el único que crece: ahí la escala marca el arranque.
   */
  const growsWithContent = () => Boolean(props.multiple && props.visualized)

  const itemPaddingBySize = () => {
    switch (props.size) {
      case 'xs':
        return 'px-3 py-1'
      case 'sm':
        return 'px-3 py-1.5'
      case 'lg':
        return 'px-3 py-2.5'
      case 'xl':
        return 'px-3 py-3'
      default:
        return 'px-3 py-2'
    }
  }

  const renderSelected = () => {
    const values = getSelectedLabels()
    if (props.multiple) {
      if (props.visualized) {
        return (
          <For each={values}>
            {item =>
              item && (
                <Chip color="primary" variant="soft" size={chipSize()} removable onRemove={e => removeSelected(item.value, e)}>
                  {item.label}
                </Chip>
              )
            }
          </For>
        )
      }
      const labels = values
        .map(v => v?.label)
        .slice(0, 3)
        .join(', ')
      const extra = values.length > 3 ? ` +${values.length - 3}` : ''
      return (
        <span class="text-foreground">
          {labels}
          {extra}
        </span>
      )
    }
    return <span class="text-foreground">{values[0]?.label}</span>
  }

  return (
    <div
      ref={el => {
        containerRef = el
      }}
      class={twMerge('relative', props.autoWidth ? 'w-auto' : 'w-full', props.class || '')}
    >
      <div
        ref={el => (triggerRef = el)}
        id={id}
        aria-labelledby={labelId}
        onClick={toggleDropdown}
        onFocus={() => !props.disabled && setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        tabIndex={props.disabled ? -1 : 0}
        class={twMerge(
          getVariantClasses(),
          `${growsWithContent() ? sizeConfig().minHeight : sizeConfig().height} ${sizeConfig().text} text-foreground rounded-lg`,
          'flex items-center w-full',
          props.label ? sizeConfig().labelPad : 'py-0',
          sizeConfig().padX,
          props.disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
          'transition-colors relative border focus:outline-none',
          // Mismo foco que Input y Textarea.
          props.disabled ? '' : 'focus:border-primary',
          resolvedError() ? 'border-danger!' : ''
        )}
        role="combobox"
        aria-expanded={isOpen()}
        aria-haspopup="listbox"
        aria-describedby={`${id}-message`}
      >
        <div class="flex flex-wrap gap-1 items-center min-h-4.5 flex-1 overflow-hidden">
          <Show when={props.startIcon}>
            <span class="shrink-0">{typeof props.startIcon === 'string' ? <Icon icon={props.startIcon} size={sizeConfig().icon} /> : props.startIcon}</span>
          </Show>
          <Show when={hasValue()} fallback={<span class="text-slate-400 select-none truncate">{props.placeholder}</span>}>
            {renderSelected()}
          </Show>
        </div>
        {/* `adornmentChild` encoge el botón que se incruste aquí (un "Nuevo…"),
            para que no llene el disparador de borde a borde. */}
        <div class={twMerge('rounded-lg absolute top-1/2 -translate-y-1/2 right-0 flex items-center pr-2 pointer-events-none w-16 justify-end', sizeConfig().adornmentChild)}>
          <div class="flex items-center gap-1 pointer-events-auto">
            <Show when={props.endIcon}>
              <span class="shrink-0 pointer-events-auto">{typeof props.endIcon === 'string' ? <Icon icon={props.endIcon} size={sizeConfig().icon} /> : props.endIcon}</span>
            </Show>
            <Show when={props.clearable && hasValue()}>
              <Button
                variant="text"
                size="sm"
                color="default"
                icon="material-symbols-light:close"
                class="p-1"
                onClick={e => {
                  e.stopPropagation()
                  const newVal: valueType = props.multiple ? [] : ''
                  setSelectedState(newVal)
                  props.onChange?.(newVal)
                }}
                aria-label="Clear"
              />
            </Show>
            <Icon icon="material-symbols-light:expand-more" size={20} class={twMerge('text-foreground-muted transition-transform duration-200', isOpen() ? 'rotate-180' : '')} />
          </div>
        </div>
      </div>

      <Show when={props.label}>
        <span
          id={labelId}
          onClick={toggleDropdown}
          class={twMerge(
            'absolute block select-none whitespace-nowrap duration-100 transition-all origin-left z-10',
            'max-w-[calc(100%-3rem)] overflow-hidden text-ellipsis',
            sizeConfig().padStart,
            resolvedError() ? 'text-danger' : 'text-foreground',
            // En reposo va centrada sobre el texto; al haber valor sube al hueco
            // de arriba. El disparador tiene altura fija, así que basta con el
            // 50 % para cualquier talla.
            // Siempre arriba: en reposo se pondria justo encima del texto y
            // taparia al `placeholder`, que es quien dice que hay que elegir.
            `${sizeConfig().labelFloating} top-1 translate-y-0`,
            props.disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          {props.label}
        </span>
      </Show>

      <FieldMessage id={`${id}-message`} size={props.size} error={resolvedError()} helperText={props.helperText} />

      {/* Hidden input for form integration */}
      <Show when={props.name}>
        {/* `data-json` avisa al `<Form>` de que aqui viaja una lista serializada */}
        <input
          ref={hiddenRef}
          type="hidden"
          name={props.name}
          data-json={props.multiple ? '' : undefined}
          value={props.multiple ? JSON.stringify(selectedState() || []) : (selectedState() || '').toString()}
        />
      </Show>

      <DropdownMenu />
    </div>
  )
}

export default Select
